import { useState } from "react";
import { Link } from "react-router";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import type { PlanComment } from "../../lib/goalsApi";
import { ProductMark, type ProductRef } from "./FlowHead";

/** The prototype's "Which product is it for?" sheet. Picking one starts the goal step for it. */
export function ProductSheet({ products, onPick, onClose }: { products: ProductRef[]; onPick: (id: string) => void; onClose: () => void }) {
  return (
    <Sheet open onClose={onClose} label="Which product">
      <div className="eyebrow">New goal</div>
      <h2 style={{ marginTop: 6 }}>Which product is it for?</h2>
      <div className="prods" style={{ marginTop: 14 }}>
        {products.map((p) => (
          <button type="button" className="prodc" key={p.id} onClick={() => onPick(p.id)}>
            <ProductMark product={p} />
            <b>{p.name}</b>
          </button>
        ))}
        <Link className="prodc add" to="/products/new">
          <Icon name="plus" />
          Add a product
        </Link>
      </div>
    </Sheet>
  );
}

/**
 * The prototype's "Ask for changes" sheet. The owner's comments on the full plan go to the agent, which writes
 * the next version; with no comments yet, the owner says in a line what should change.
 */
export function ChangesSheet({
  open,
  onClose,
  unsent,
  busy,
  onSend,
}: {
  open: boolean;
  onClose: () => void;
  unsent: PlanComment[];
  busy: boolean;
  onSend: (ask: string | null) => void;
}) {
  const [ask, setAsk] = useState("");
  const n = unsent.length;
  return (
    <Sheet open={open} onClose={onClose} label="Ask for changes">
      <h2>Ask for changes</h2>
      <p className="lede" style={{ fontSize: "var(--fs-text)", marginTop: 6 }}>
        The agent answers with a new version of the plan, and you decide.
      </p>
      {n > 0 ? (
        <div className="card" style={{ marginTop: 14, padding: 14 }}>
          <b style={{ fontSize: "var(--fs-text)" }}>
            Your {n} comment{n > 1 ? "s" : ""}
          </b>
          {unsent.map((c) => (
            <div className="cnote" key={c.id}>
              <Icon name="reply" />
              <span>
                {c.section}: {c.body}
              </span>
            </div>
          ))}
          <button type="button" className="btn sm" style={{ marginTop: 12 }} disabled={busy} onClick={() => onSend(null)}>
            Send to the agent
          </button>
        </div>
      ) : (
        <>
          <label className="fl" htmlFor="chAsk">
            What should change?
          </label>
          <textarea className="field" id="chAsk" rows={3} value={ask} onChange={(e) => setAsk(e.target.value)} />
          <div className="m-act">
            <button type="button" className="btn sm" disabled={busy || !ask.trim()} onClick={() => onSend(ask.trim())}>
              Send to the agent
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
